/**
 * Respaldo de una interrupción (Manual SISS): aviso a usuarios, aviso a la
 * SISS, fotos. El archivo se sube a /uploads (kind interrupcion_evidencia) y
 * luego se asocia a la interrupción.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Paperclip, Trash2, Upload } from "lucide-react";

import { ApiError, apiRequest, apiUploadFile } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { Button, Cargando, Select } from "../../components/ui";

type TipoEvidencia = "aviso_usuarios" | "aviso_siss" | "foto" | "otro";

interface Evidencia {
  key: string;
  nombre: string;
  tipo: TipoEvidencia;
  subido_at: string;
  subido_por: string | null;
  url: string | null;
}

const TIPOS: Record<TipoEvidencia, string> = {
  aviso_usuarios: "Aviso a usuarios",
  aviso_siss: "Aviso a la SISS",
  foto: "Foto",
  otro: "Otro",
};

export function RespaldoInterrupcionModal({
  interrupcionId,
  numero,
  onClose,
}: {
  interrupcionId: number;
  numero: number;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [tipo, setTipo] = useState<TipoEvidencia>("aviso_usuarios");
  const base = `/api/v1/continuidad/agua/interrupciones/${interrupcionId}/evidencias`;

  const listaQ = useQuery({
    queryKey: ["evidencias", interrupcionId],
    queryFn: () => apiRequest<Evidencia[]>(base),
  });
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ["evidencias", interrupcionId] });
    void qc.invalidateQueries({ queryKey: ["interrupciones"] });
  };

  const subir = useMutation({
    mutationFn: async (archivo: File) => {
      const up = await apiUploadFile("interrupcion_evidencia", archivo, {
        filename: archivo.name,
        contentType: archivo.type,
      });
      return apiRequest(base, {
        method: "POST",
        body: { storage_key: up.key, nombre: archivo.name, tipo },
      });
    },
    onSuccess: () => {
      refrescar();
      toast.success("Respaldo agregado");
      if (inputRef.current) inputRef.current.value = "";
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo subir"),
  });
  const quitar = useMutation({
    mutationFn: (key: string) =>
      apiRequest(`${base}/quitar`, { method: "POST", body: { storage_key: key } }),
    onSuccess: refrescar,
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo quitar"),
  });

  const lista = listaQ.data ?? [];
  return (
    <Modal
      open
      onClose={onClose}
      title={`Respaldo de la interrupción N° ${numero}`}
      subtitle="Aviso a usuarios, aviso a la SISS y fotos (PDF o imagen, hasta 20 MB)."
      icon={Paperclip}
    >
      <div className="space-y-4 text-sm">
        {listaQ.isLoading ? (
          <Cargando inline />
        ) : lista.length === 0 ? (
          <p className="rounded border border-dashed border-neutral-300 p-4 text-center text-neutral-500">
            Sin respaldo todavía.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-100 rounded border border-neutral-200">
            {lista.map((e) => (
              <li key={e.key} className="flex items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-neutral-800">{e.nombre}</div>
                  <div className="text-xs text-neutral-500">
                    {TIPOS[e.tipo]} · {new Date(e.subido_at).toLocaleString("es-CL")}
                    {e.subido_por ? ` · ${e.subido_por}` : ""}
                  </div>
                </div>
                {e.url && (
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
                  >
                    Ver <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                <Button
                  size="sm"
                  variant="linkDanger"
                  icon={Trash2}
                  iconOnly
                  aria-label="Quitar"
                  onClick={() => quitar.mutate(e.key)}
                />
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Select
            label="Tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoEvidencia)}
            containerClassName="w-48"
          >
            {(Object.keys(TIPOS) as TipoEvidencia[]).map((t) => (
              <option key={t} value={t}>
                {TIPOS[t]}
              </option>
            ))}
          </Select>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) subir.mutate(f);
            }}
          />
          <Button icon={Upload} disabled={subir.isPending} onClick={() => inputRef.current?.click()}>
            {subir.isPending ? "Subiendo…" : "Subir archivo"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
